/* Shared item-key updates, gift detection, and merchandise totals.
   v38: the free bronze pendant is added by EG Auto Add to Cart and made free by its own app
   discount ("Free Bronze Pendant"), which targets the exact line EG tagged with _Gifted.
   The theme no longer applies a gift code, and each cart write uses the cart Shopify returns
   instead of reading it again, so a change is one request instead of up to seven. */
(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) {
    const config = JSON.parse(root.document.getElementById('ch-cart-config').textContent);
    root.CHCartState = api.create(async (path, payload) => {
      const response = await root.fetch(config.root + path, {
        method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: {'Accept': 'application/json', ...(payload ? {'Content-Type': 'application/json'} : {})},
        ...(payload ? {body: JSON.stringify(payload)} : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Your cart could not be updated. Please try again.');
      return data;
    }, config);
  }
})(typeof window !== 'undefined' ? window : null, function() {
  // A gift is any line EG added (it carries the _Gifted property).
  const isGift = item => !!(item.properties && (item.properties._Gifted || item.properties._ch_bonus));
  const codes = cart => (cart.discount_codes || []).filter(d => d.applicable).map(d => d.code);
  const uniqueCodes = values => [...new Map(values.filter(Boolean).map(c => [c.toLowerCase(), c])).values()];
  // v62: cart.js lists order discounts flat; cart/update.js and cart/change.js wrap each one as
  // {amount, discount_application}. Read both, or a new code shows $0 off until the page reloads.
  function orderDiscounts(cart) {
    return (cart.cart_level_discount_applications || []).map(d => {
      const app = d.discount_application || d;
      return {title: app.title, target: app.target_type, selection: app.target_selection,
              amount: Number(d.amount != null ? d.amount : app.total_allocated_amount) || 0};
    }).filter(d => d.target !== 'shipping_line' && d.amount > 0);
  }
  // Each line's share of the order discounts, split by line price the way Shopify spreads an
  // "across" discount. Discounts on chosen products only can't be placed, so they stay in the subtotal.
  function lineShares(cart) {
    const items = cart.items || [];
    const shares = items.map(() => ({amount: 0, titles: []}));
    orderDiscounts(cart).filter(d => d.selection === 'all').forEach(d => {
      const base = items.reduce((n, i) => n + i.final_line_price, 0);
      if (!base) return;
      let left = d.amount, last = -1;
      items.forEach((i, n) => {
        if (i.final_line_price <= 0) return;
        const part = Math.floor(d.amount * i.final_line_price / base);
        shares[n].amount += part; shares[n].titles.push(d.title); left -= part; last = n;
      });
      if (last >= 0) shares[last].amount += left;
    });
    return shares;
  }
  function totals(cart) {
    const original = (cart.items || []).reduce((n, i) => n + i.original_line_price, 0);
    const lines = (cart.items || []).reduce((n, i) => n + i.final_line_price, 0);
    const order = orderDiscounts(cart).reduce((n, d) => n + d.amount, 0);
    const subtotal = Math.max(0, lines - order);
    return {subtotal, savings: Math.max(0, original - subtotal)};
  }
  function create(request) {
    let queue = Promise.resolve();
    const enqueue = job => { const next = queue.then(job); queue = next.catch(() => {}); return next; };
    const get = () => request('cart.js');
    // cart/change.js and cart/update.js both return the full cart.
    const write = (path, body) => request(path, body);
    return {
      totals, lineShares, isGift,
      refresh: () => enqueue(get),
      // Unknown keys make cart/change.js return an error, so a stale drawer can't change the wrong line.
      change: (key, quantity) => enqueue(() => write('cart/change.js', {id: key, quantity})),
      update: (updates, note) => enqueue(() => write('cart/update.js', {updates, ...(note !== undefined ? {note} : {})})),
      discount: code => enqueue(async () => {
        const before = await get();
        const previous = codes(before);
        let cart = await write('cart/update.js', {discount: uniqueCodes([...previous, code]).join(',')});
        const valid = codes(cart).some(c => c.toLowerCase() === code.toLowerCase());
        // A code that doesn't combine with product discounts can knock out the free gift; keep the gift free.
        const freeGiftKeys = before.items.filter(i => isGift(i) && i.final_line_price === 0).map(i => i.key);
        const giftCharged = cart.items.some(i => freeGiftKeys.includes(i.key) && i.final_line_price > 0);
        if (!valid || giftCharged) {
          cart = await write('cart/update.js', {discount: previous.join(',')});
          return {cart, valid: false};
        }
        return {cart, valid};
      }),
      removeDiscount: () => enqueue(() => write('cart/update.js', {discount: ''}))
    };
  }
  return {create, totals, lineShares, isGift};
});
